import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { Plus, Edit2, Trash2, X, Upload, Users, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { aboutService, siteContentService, authService } from '../services/api';

function AdminEditorialBoard() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    email: '',
    phone: '',
    bio: '',
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);

  const [about, setAbout] = useState({
    id: null,
    introduction: '',
    editorialBoardDesc: '',
    objectives: [],
    policies: [],
    specializedFields: [],
  });
  const [editingDesc, setEditingDesc] = useState(false);
  const [tempDesc, setTempDesc] = useState('');

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const response = await aboutService.get();
        if (response.data) {
          setAbout({
            id: response.data.id,
            introduction: response.data.introduction || '',
            editorialBoardDesc: response.data.editorialBoardDesc || '',
            objectives: response.data.objectives || [],
            policies: response.data.policies || [],
            specializedFields: response.data.specializedFields || [],
          });
        }
      } catch (error) {
        console.error('Error fetching about data:', error);
      }
    };
    fetchAbout();

    const fetchMembers = async () => {
      try {
        const response = await siteContentService.get('EDITORIAL_BOARD');
        if (response.data && response.data.value) {
          const parsed = JSON.parse(response.data.value);
          if (Array.isArray(parsed)) {
            setMembers(parsed);
          }
        }
      } catch (error) {
        console.error('Error fetching editorial board:', error);
      }
    };
    fetchMembers();
  }, []);

  const saveMembers = async (updatedMembers) => {
    try {
      await siteContentService.set('EDITORIAL_BOARD', { value: JSON.stringify(updatedMembers) });
    } catch (error) {
      console.error('Error saving editorial board:', error);
    }
  };

  const handleSaveDesc = async () => {
    if (!about.id) return;
    try {
      const payload = {
        introduction: about.introduction,
        editorialBoardDesc: tempDesc,
        objectives: about.objectives,
        policies: about.policies,
        specializedFields: about.specializedFields,
      };
      await aboutService.update(about.id, payload);
      setAbout({ ...about, editorialBoardDesc: tempDesc });
      setEditingDesc(false);
    } catch (error) {
      console.error('Error saving editorial description:', error);
    }
  };

  const handleCancelDesc = () => {
    setTempDesc(about.editorialBoardDesc);
    setEditingDesc(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let updatedMembers;
    if (editingId) {
      updatedMembers = members.map(m =>
        m.id === editingId
          ? { ...m, ...formData, image: imagePreview || m.image }
          : m
      );
      setEditingId(null);
    } else {
      const newMember = {
        id: Math.max(...members.map(m => m.id), 0) + 1,
        ...formData,
        image: imagePreview,
      };
      updatedMembers = [...members, newMember];
    }
    
    setMembers(updatedMembers);
    saveMembers(updatedMembers);

    setFormData({
      name: '',
      position: '',
      email: '',
      phone: '',
      bio: '',
      image: null,
    });
    setImagePreview(null);
    setShowForm(false);
  };

  const handleEdit = (member) => {
    setFormData({
      name: member.name,
      position: member.position,
      email: member.email,
      phone: member.phone,
      bio: member.bio,
      image: member.image,
    });
    setImagePreview(member.image);
    setEditingId(member.id);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      const updatedMembers = members.filter(m => m.id !== id);
      setMembers(updatedMembers);
      saveMembers(updatedMembers);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      name: '',
      position: '',
      email: '',
      phone: '',
      bio: '',
      image: null,
    });
    setImagePreview(null);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-5xl font-bold mb-2">مدیریت هیئت تحریریه</h1>
              <p className="text-xl text-blue-100">افزودن، ویرایش و حذف اعضای هیئت تحریریه</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition"
            >
              <LogOut size={20} />
              خروج
            </button>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Editorial Board Description */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">توضیح هیئت تحریریه</h2>

          {editingDesc ? (
            <div className="space-y-4">
              <textarea
                value={tempDesc}
                onChange={(e) => setTempDesc(e.target.value)}
                rows="6"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 font-medium"
              />
              <div className="flex gap-4">
                <button
                  onClick={handleSaveDesc}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition"
                >
                  ذخیره
                </button>
                <button
                  onClick={handleCancelDesc}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                >
                  انصراف
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-gray-700 leading-relaxed mb-6 text-lg">{about.editorialBoardDesc || 'اطلاعاتی برای نمایش وجود ندارد'}</p>
              <button
                onClick={() => {
                  setEditingDesc(true);
                  setTempDesc(about.editorialBoardDesc);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
              >
                <Edit2 size={18} />
                ویرایش توضیح
              </button>
            </div>
          )}
        </div>

        {/* Add Member Button */}
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="mb-8 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition shadow-lg"
          >
            <Plus size={20} />
            افزودن عضو جدید
          </button>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingId ? 'ویرایش عضو' : 'افزودن عضو جدید'}
              </h2>
              <button
                onClick={handleCancel}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={24} className="text-gray-600" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">نام و نام خانوادگی</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="نام عضو"
                  />
                </div>

                {/* Position */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">سمت</label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="مثال: سردبیر، سردبیر اجرایی"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">ایمیل</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="email@majraye.ir"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">تلفن</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="021-12345678"
                  />
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">بیوگرافی</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  placeholder="توضیحات کوتاه درباره عضو"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">تصویر</label>
                <div className="flex gap-6">
                  <div className="flex-1">
                    <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:bg-blue-50 transition">
                      <Upload size={20} className="text-blue-600" />
                      <span className="text-blue-600 font-semibold">انتخاب تصویر</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {imagePreview && (
                    <div className="w-32 h-32 rounded-lg overflow-hidden border border-gray-300">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-4 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-bold hover:from-blue-700 hover:to-blue-800 transition"
                >
                  {editingId ? 'ذخیره تغییرات' : 'افزودن عضو'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                >
                  انصراف
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Members Grid */}
        {members.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Users size={48} className="mx-auto mb-4 opacity-30" />
            <p>هیچ عضوی برای نمایش وجود ندارد</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map(member => (
              <div key={member.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition">
                {/* Image */}
                {member.image ? (
                  <div className="h-48 bg-gray-200 overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-blue-200 to-blue-300 flex items-center justify-center">
                    <Users size={48} className="text-blue-600 opacity-50" />
                  </div>
                )}

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-blue-600 font-semibold mb-3">{member.position}</p>
                  
                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <p><span className="font-semibold">ایمیل:</span> {member.email}</p>
                    <p><span className="font-semibold">تلفن:</span> {member.phone}</p>
                    {member.bio && <p><span className="font-semibold">بیوگرافی:</span> {member.bio}</p>}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleEdit(member)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold text-sm"
                    >
                      <Edit2 size={16} />
                      ویرایش
                    </button>
                    <button
                      onClick={() => handleDelete(member.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold text-sm"
                    >
                      <Trash2 size={16} />
                      حذف
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminEditorialBoard;
